import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Button, Input } from "reactstrap";
import { toast } from "react-toastify";
import UserContext from "../auth/UserContext";
import "./ElectronicTipWalletPage.css";

const STORAGE_PREFIX = "electronicTipWallet.v1";
const MAX_CARD_COUNT = 15;
const MAX_FILE_BYTES = 3 * 1024 * 1024;

function buildStorageKey(userDetail) {
    const username =
        String(userDetail?.username || "")
            .trim()
            .toLowerCase() || "unknown";
    return `${STORAGE_PREFIX}.${username}`;
}

function toDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ""));
        reader.onerror = () => reject(new Error("Unable to read file"));
        reader.readAsDataURL(file);
    });
}

function clampIndex(index, total) {
    if (total <= 0) return 0;
    if (index < 0) return 0;
    if (index > total - 1) return total - 1;
    return index;
}

function readSavedCards(storageKey) {
    try {
        const raw = window.localStorage.getItem(storageKey);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];

        return parsed
            .map((row) => ({
                id: String(row?.id || ""),
                name: String(row?.name || ""),
                imageDataUrl: String(row?.imageDataUrl || ""),
                createdAt: String(row?.createdAt || ""),
            }))
            .filter((row) => row.id && row.imageDataUrl.startsWith("data:image/"));
    } catch {
        return [];
    }
}

const ElectronicTipWalletPage = () => {
    const { userDetail } = useContext(UserContext);
    const roleId = Number(userDetail?.roleId || 0);
    const canUseTipWallet = roleId >= 1 && roleId <= 3;

    const storageKey = useMemo(() => buildStorageKey(userDetail), [userDetail]);
    const fileInputRef = useRef(null);
    const hasHydratedRef = useRef(false);

    const [cards, setCards] = useState([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const [persistWarning, setPersistWarning] = useState("");

    const activeCard = cards[activeIndex] || null;

    useEffect(() => {
        const saved = readSavedCards(storageKey);
        setCards(saved);
        setActiveIndex(0);
        setPersistWarning("");
        hasHydratedRef.current = true;
    }, [storageKey]);

    useEffect(() => {
        if (!hasHydratedRef.current) return;

        try {
            window.localStorage.setItem(storageKey, JSON.stringify(cards));
            setPersistWarning("");
        } catch {
            setPersistWarning("This device is out of local storage for QR images. Remove a few images and try again.");
        }
    }, [cards, storageKey]);

    async function handleAddImages(evt) {
        const files = Array.from(evt.target.files || []);
        evt.target.value = "";

        if (!files.length) return;

        const slotsRemaining = Math.max(0, MAX_CARD_COUNT - cards.length);
        if (slotsRemaining <= 0) {
            toast.error(`Tip wallet is full. Remove an image before adding more (max ${MAX_CARD_COUNT}).`, { autoClose: 2000 });
            return;
        }

        const acceptedFiles = files
            .filter((file) => String(file.type || "").startsWith("image/"))
            .filter((file) => Number(file.size || 0) <= MAX_FILE_BYTES)
            .slice(0, slotsRemaining);

        if (!acceptedFiles.length) {
            toast.error("No valid images found. Use image files up to 3MB each.", { autoClose: 2200 });
            return;
        }

        const nextCards = [];

        for (const file of acceptedFiles) {
            try {
                const imageDataUrl = await toDataUrl(file);
                nextCards.push({
                    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                    name: file.name || "Tip QR",
                    imageDataUrl,
                    createdAt: new Date().toISOString(),
                });
            } catch {
                toast.error(`Unable to read ${file.name || "selected file"}.`, { autoClose: 1800 });
            }
        }

        if (!nextCards.length) return;

        setCards((prev) => {
            const updated = [...prev, ...nextCards];
            setActiveIndex(updated.length - 1);
            return updated;
        });

        if (files.length > acceptedFiles.length) {
            toast.info(`Added ${acceptedFiles.length} image(s). Some files were skipped for size/type/limit.`, { autoClose: 2200 });
        } else {
            toast.success(`Added ${acceptedFiles.length} tip QR image(s).`, { autoClose: 1600 });
        }
    }

    function handleRemoveActive() {
        if (!activeCard) return;

        setCards((prev) => {
            const updated = prev.filter((_, idx) => idx !== activeIndex);
            const nextIndex = clampIndex(activeIndex, updated.length);
            setActiveIndex(nextIndex);
            return updated;
        });

        toast.success("Removed selected tip QR image.", { autoClose: 1400 });
    }

    function movePrev() {
        if (!cards.length) return;
        setActiveIndex((prev) => (prev - 1 + cards.length) % cards.length);
    }

    function moveNext() {
        if (!cards.length) return;
        setActiveIndex((prev) => (prev + 1) % cards.length);
    }

    function openFilePicker() {
        fileInputRef.current?.click();
    }

    if (!canUseTipWallet) {
        return (
            <section className="tip-wallet-page page-scroll">
                <div className="tip-wallet-inner page-scroll-inner">
                    <div className="tip-wallet-card-shell">
                        <h3>Electronic Tip Wallet</h3>
                        <p>This feature is available for valet, supervisor, and manager users.</p>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="tip-wallet-page page-scroll">
            <div className="tip-wallet-inner page-scroll-inner">
                <div className="tip-wallet-card-shell">
                    <div className="tip-wallet-header-row">
                        <div>
                            <h3>Electronic Tip Wallet</h3>
                            <p>Upload screenshots of your personal tip QR codes and swipe through them when a customer asks to tip electronically.</p>
                        </div>
                        <div className="tip-wallet-actions">
                            <Button
                                color="primary"
                                type="button"
                                onClick={openFilePicker}>
                                Add
                            </Button>
                            <Button
                                color="danger"
                                type="button"
                                onClick={handleRemoveActive}
                                disabled={!activeCard}>
                                Remove
                            </Button>
                        </div>
                    </div>

                    <Input
                        innerRef={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="tip-wallet-hidden-input"
                        onChange={handleAddImages}
                    />

                    <div className="tip-wallet-guidance">
                        <div className="tip-wallet-guidance-title">Storage Tips</div>
                        <ul className="tip-wallet-guidance-list">
                            <li>Most valets only need 1 to 4 codes (for example Venmo, Cash App, and one backup option).</li>
                            <li>Crop tightly around the QR code before upload to save storage.</li>
                            <li>Avoid full-screen screenshots. QR-only images let you store more cards.</li>
                            <li>
                                Current limit: up to {MAX_CARD_COUNT} images, {Math.round(MAX_FILE_BYTES / (1024 * 1024))}MB each.
                            </li>
                        </ul>
                    </div>

                    {persistWarning ? <div className="tip-wallet-warning">{persistWarning}</div> : null}

                    <div className="tip-wallet-preview-shell">
                        {activeCard ? (
                            <>
                                <div className="tip-wallet-preview-meta">
                                    <strong>{activeCard.name}</strong>
                                    <span>
                                        {activeIndex + 1} of {cards.length}
                                    </span>
                                </div>
                                <div className="tip-wallet-preview-frame">
                                    <img
                                        src={activeCard.imageDataUrl}
                                        alt={`Electronic tip QR ${activeIndex + 1}`}
                                        className="tip-wallet-preview-image"
                                    />
                                </div>
                            </>
                        ) : (
                            <div className="tip-wallet-empty">
                                <h5>No tip QR images yet</h5>
                                <p>Select Add and upload a screenshot from your phone or desktop. For best storage, crop to the QR only.</p>
                            </div>
                        )}
                    </div>

                    <div className="tip-wallet-carousel-row">
                        <button
                            type="button"
                            className="tip-wallet-nav-btn"
                            onClick={movePrev}
                            disabled={cards.length <= 1}
                            aria-label="Previous tip QR image">
                            {"<"}
                        </button>

                        <div className="tip-wallet-stack">
                            {cards.map((card, idx) => {
                                const delta = idx - activeIndex;
                                const absDelta = Math.abs(delta);
                                const visible = absDelta <= 2;
                                if (!visible) return null;

                                const x = delta * 72;
                                const y = absDelta * 10;
                                const scale = idx === activeIndex ? 1 : 1 - absDelta * 0.08;
                                const rotate = delta * 5;
                                const opacity = idx === activeIndex ? 1 : 0.62;

                                return (
                                    <button
                                        key={card.id}
                                        type="button"
                                        className={`tip-wallet-mini-card${idx === activeIndex ? " is-active" : ""}`}
                                        style={{
                                            transform: `translateX(${x}px) translateY(${y}px) scale(${scale}) rotate(${rotate}deg)`,
                                            zIndex: 50 - absDelta,
                                            opacity,
                                        }}
                                        onClick={() => setActiveIndex(idx)}
                                        aria-label={`Select tip QR image ${idx + 1}`}>
                                        <img
                                            src={card.imageDataUrl}
                                            alt={card.name || `Tip QR ${idx + 1}`}
                                        />
                                    </button>
                                );
                            })}
                        </div>

                        <button
                            type="button"
                            className="tip-wallet-nav-btn"
                            onClick={moveNext}
                            disabled={cards.length <= 1}
                            aria-label="Next tip QR image">
                            {">"}
                        </button>
                    </div>

                    <div className="tip-wallet-hint">Keep only your current payment accounts here. These images stay on this device/browser session storage.</div>
                </div>
            </div>
        </section>
    );
};

export default ElectronicTipWalletPage;
